import type { paths } from '@planetra/api-client';

/**
 * Who the browser is acting as (P0-WEB-07).
 *
 * A store outside React, for the same reason `portal-client.ts` is: the
 * organisation header is attached by the transport, which runs in loaders and
 * has no access to a hook. React reads it through `useSyncExternalStore`.
 *
 * Bootstrapped from `GET /v1/auth/me` (P0-PLAT-20) and `GET /v1/organisations`
 * together: one says who the session belongs to, the other what they may act
 * in. Both answer 401 when there is no session, so either is a truthful probe —
 * they are fetched as a pair because the shell needs both before it paints.
 *
 * Caching the login response instead of asking would be worse than a round
 * trip: it survives revocation and misses a sign-out in another tab, so it
 * would report a session that no longer exists.
 */

type OrganisationsResponse =
  paths['/v1/organisations']['get']['responses'][200]['content']['application/json'];

/** Derived from the generated schema, so a field added to the API arrives here
 *  without anybody re-declaring it. */
export type Organisation = OrganisationsResponse['data'][number];

/** The same shape `POST /v1/auth/login` returns — one declaration on the API
 *  side (`AuthenticatedUserNode`), so the two can never describe different
 *  users. */
export type CurrentUser =
  paths['/v1/auth/me']['get']['responses'][200]['content']['application/json'];

export type SessionStatus =
  /** Nothing has been checked yet — the first loader has not resolved. */
  'unknown' | 'authenticated' | 'anonymous';

export interface SessionState {
  readonly status: SessionStatus;
  /** Who the session belongs to. Absent until the first probe resolves. */
  readonly user: CurrentUser | undefined;
  readonly organisations: readonly Organisation[];
  /**
   * The organisation every request acts for (P0-PLAT-10). A claim checked
   * against membership on the server, so a wrong value fails rather than
   * leaking — but it is validated against the list here anyway, because a 403
   * on every request is a worse way to discover a stale preference.
   */
  readonly organisationId: string | undefined;
}

const ANONYMOUS: SessionState = {
  status: 'anonymous',
  user: undefined,
  organisations: [],
  organisationId: undefined,
};
const UNKNOWN: SessionState = {
  status: 'unknown',
  user: undefined,
  organisations: [],
  organisationId: undefined,
};

export interface SessionStore {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => SessionState;
  /** Record a successful probe: who they are, and what they may act in. */
  readonly authenticated: (user: CurrentUser, organisations: readonly Organisation[]) => void;
  /** Record a 401, or a sign-out. */
  readonly clear: () => void;
  readonly selectOrganisation: (organisationId: string) => void;
}

/** Where the choice survives a reload. Not a security boundary — the server
 *  checks membership on every request regardless of what is stored here. */
export const ORGANISATION_STORAGE_KEY = 'planetra.organisation';

export function createSessionStore(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined = browserStorage(),
): SessionStore {
  const preference = guarded(storage);

  let state = UNKNOWN;
  const listeners = new Set<() => void>();

  function publish(next: SessionState): void {
    state = next;
    for (const listener of listeners) listener();
  }

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getSnapshot() {
      return state;
    },

    authenticated(user, organisations) {
      publish({
        status: 'authenticated',
        user,
        organisations,
        organisationId: chooseOrganisation(organisations, preference.read()),
      });
    },

    clear() {
      // The stored preference goes too. Leaving it would send a previous
      // account's organisation id on the next session's first request, which
      // fails closed but fails confusingly.
      preference.clear();
      publish(ANONYMOUS);
    },

    selectOrganisation(organisationId) {
      if (!state.organisations.some((organisation) => organisation.id === organisationId)) return;
      preference.write(organisationId);
      publish({ ...state, organisationId });
    },
  };
}

/**
 * The stored choice if it is still one the account may act in, otherwise the
 * first available, otherwise none.
 *
 * Falling back to "their first organisation" is only safe as a *default*, never
 * as a recovery from an unusable claim — `ResolveOrganisation` in apps/api
 * makes the same argument from the other side: guessing would put a
 * consultant's work in whichever council sorted first. Here the user has not
 * chosen yet, so a default is a default.
 */
function chooseOrganisation(
  organisations: readonly Organisation[],
  stored: string | null | undefined,
): string | undefined {
  if (stored != null && organisations.some((organisation) => organisation.id === stored)) {
    return stored;
  }
  return organisations[0]?.id;
}

/**
 * Every access guarded, the way `packages/i18n`'s locale store does it.
 *
 * `localStorage` is not simply present or absent. Safari in private browsing
 * and any page under a blocked-cookies policy throw on access rather than
 * returning null, and a partial implementation — an object with some of the
 * methods — is common enough in test and embedded environments to be worth
 * surviving. Remembering which organisation was selected is not worth taking
 * the session down for; the server checks membership on every request whatever
 * is stored here.
 */
function guarded(storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined) {
  return {
    read(): string | null {
      try {
        return storage?.getItem(ORGANISATION_STORAGE_KEY) ?? null;
      } catch {
        return null;
      }
    },
    write(organisationId: string): void {
      try {
        storage?.setItem(ORGANISATION_STORAGE_KEY, organisationId);
      } catch {
        // Not persisted. The selection still applies for this session.
      }
    },
    clear(): void {
      try {
        storage?.removeItem(ORGANISATION_STORAGE_KEY);
      } catch {
        // Nothing to do — and nothing worth failing a sign-out over.
      }
    },
  };
}

function browserStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}
